import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config'; // Adjust path
import styles from './AiSearchAssistant.module.css';
import { Mic, X, Send, Bot, Loader2, Sparkles } from 'lucide-react';

const AiSearchAssistant = () => {
    const navigate = useNavigate();
    
    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [voiceStatus, setVoiceStatus] = useState("idle"); 
    const messagesEndRef = useRef(null);

    // Search Flow State
    const [step, setStep] = useState("init"); // init -> profession -> location -> redirect
    const [searchParams, setSearchParams] = useState({
        professionId: null,
        professionName: "",
        locationName: "",
        lat: null,
        lng: null
    });
    
    const [suggestedProfessions, setSuggestedProfessions] = useState([]);

    // Auto-scroll
// Auto-scroll
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // --- TTS & STT ---
    const speak = async (text) => {
        try {
            setVoiceStatus("speaking");
            const response = await axios.post(
                `${API_BASE_URL}/api/AiSearch/speak`, 
                { text: text }, 
                { responseType: 'blob' }
            );
            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);
            audio.play();
            audio.onended = () => setVoiceStatus("idle");
            audio.onerror = () => setVoiceStatus("idle");
        } catch (error) {
            console.error("TTS Error", error);
            setVoiceStatus("idle");
        }
    };

    const listen = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            addBotMessage("Voice input isn't supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        setVoiceStatus("listening");
        recognition.start();

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInputText(transcript);
            handleUserResponse(transcript);
        };

        recognition.onerror = () => setVoiceStatus("idle");
        recognition.onend = () => {
            if(voiceStatus === 'listening') setVoiceStatus("idle");
        };
    };

    // --- MESSAGING HELPERS ---
    const addBotMessage = (text, options = []) => {
        setMessages(prev => [...prev, { sender: 'bot', text, options }]);
        speak(text);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { sender: 'user', text }]);
    };

    // --- LOGIC FLOW ---
    const startConversation = () => {
        setIsOpen(true);
        if (messages.length === 0) {
            setStep("profession");
            addBotMessage("Hi! I'm your Search Assistant. \n\nWhat kind of professional are you looking for? (e.g. Plumber, Electrician)");
        }
    };

    const handleUserResponse = async (text) => {
        setVoiceStatus("processing");
        addUserMessage(text);

        // --- STEP 1: RESOLVE PROFESSION ---
        if (step === "profession") {
            try {
                // 1. Search for Profession
                const res = await axios.get(`${API_BASE_URL}/api/Jobs/AISmartSearch?search=${encodeURIComponent(text)}`);
                const matches = res.data || [];

                if (matches.length > 0) {
                    setSuggestedProfessions(matches);
                    
                    // Logic: If exact match or high confidence, proceed. Else ask for clarification.
                    // For improved UX, let's offer top 3 if ambiguous, or assume top 1 if user typed distinct name.
                    if (matches.length > 1 && matches[0].name.toLowerCase() !== text.toLowerCase()) {
                        const options = matches.slice(0, 4).map(p => p.name);
                        setStep("profession_confirm");
                        addBotMessage(`I found a few matches for "${text}". Which one fits best?`, options);
                    } else {
                        // Exact match or best guess
                        selectProfession(matches[0]);
                    }
                } else {
                    addBotMessage("I couldn't find that trade. Could you try a different name? (e.g. Builder, Tiler)");
                }
            } catch (e) {
                console.error(e);
                addBotMessage("Sorry, I had trouble searching. Please try again.");
            }
        }

        // --- STEP 1b: CONFIRM PROFESSION ---
        else if (step === "profession_confirm") {
            const match = suggestedProfessions.find(p => p.name.toLowerCase() === text.toLowerCase());
            if (match) {
                selectProfession(match);
            } else {
                // User said something else, retry search
                setStep("profession");
                handleUserResponse(text); 
                return;
            }
        }

        // --- STEP 2: RESOLVE LOCATION ---
        else if (step === "location") {
            try {
                const locRes = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(text)}`);
                
                if (locRes.data && locRes.data.length > 0) {
                    const loc = locRes.data[0];
                    setSearchParams(prev => ({
                        ...prev,
                        locationName: loc.displayName, // e.g. "Cabramatta, NSW"
                        lat: loc.lat,
                        lng: loc.lon
                    }));

                    setStep("redirecting");
                    addBotMessage(`Great! Searching for ${searchParams.professionName} near ${loc.displayName}...`);
                    
                    // --- PERFORM REDIRECT ---
                    setTimeout(() => {
                        performSearchRedirect(searchParams.professionName, loc.displayName, loc.lat, loc.lon);
                    }, 2000); // Small delay for UX
                } else {
                    addBotMessage("I couldn't find that location. Please try 'Suburb, State' (e.g. Bondi, NSW).");
                }
            } catch (e) {
                addBotMessage("I'm having trouble with the map. Please type your postcode.");
            }
        }

        setVoiceStatus("idle");
    };

    // Helper to set profession and move next
    const selectProfession = (prof) => {
        setSearchParams(prev => ({ ...prev, professionId: prof.id, professionName: prof.name }));
        setStep("location");
        addBotMessage(`Okay, looking for a ${prof.name}. \n\nWhere do you need them? (Suburb or Postcode)`);
    };

    // Helper to Redirect
    const performSearchRedirect = (category, address, lat, lng) => {
        setIsOpen(false);
        // Navigate to your Search Page
        navigate(`/map-search-v2?searchTerm=${encodeURIComponent(category)}&postCode=2166&type=Profession&radius=25`);
        //navigate(`/search?category=${encodeURIComponent(category)}&location=${encodeURIComponent(address)}&lat=${lat}&lng=${lng}`);
    };

    // UI Inputs
    const handleSend = () => {
        if(!inputText.trim()) return;
        handleUserResponse(inputText);
        setInputText("");
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSend();
    };

    if (!isOpen) {
        return (
            <button className={styles.fab} onClick={startConversation} title="AI Search Assistant">
                <Bot className={styles.fabIcon} />
            </button>
        );
    }

    return (
        <div className={styles.chatWindow}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <Sparkles size={18} /> BuildStart AI Search
                </div>
                <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
                    <X size={20} />
                </button>
            </div>

            {/* Chat List */}
            <div className={styles.messageList}>
                {messages.map((msg, i) => (
                    <div key={i} className={`${styles.messageRow} ${msg.sender === 'user' ? styles.userRow : styles.botRow}`}>
                        <div className={`${styles.bubble} ${msg.sender === 'user' ? styles.userBubble : styles.botBubble}`}>
                            <div style={{whiteSpace:'pre-wrap'}}>{msg.text}</div>
                            {msg.options && (
                                <div className={styles.optionsContainer}>
                                    {msg.options.map(opt => (
                                        <button key={opt} className={styles.optionChip} onClick={() => handleUserResponse(opt)}>
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {voiceStatus === 'processing' && (
                    <div className={styles.messageRow}>
                        <div className={`${styles.bubble} ${styles.botBubble}`}>
                            <Loader2 size={16} className={styles.spin} /> Thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className={styles.inputArea}>
                <button 
                    className={`${styles.micBtn} ${voiceStatus === 'listening' ? styles.micBtnActive : ''}`}
                    onClick={listen}
                    disabled={voiceStatus === 'processing'}
                >
                    <Mic size={20} />
                </button>
                <input 
                    className={styles.input} 
                    placeholder="Type or speak..." 
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <button className={styles.micBtn} onClick={handleSend} disabled={!inputText.trim()}>
                    <Send size={18} />
                </button>
            </div>
        </div>
    );
};

export default AiSearchAssistant;